/**
 * Automatic analytics tracking via data attributes
 * 
 * Instead of manual tracking calls in components, just add data-track-* attributes:
 * 
 * <a data-track-event="contact_whatsapp_click" data-track-location="floating_button">
 * 
 * The global event listener handles all tracking automatically.
 */

import {
  trackContactWhatsApp,
  trackContactEmail,
  trackContactPhone,
  trackEvent,
} from "@/lib/analytics";

// Track if already initialized to prevent duplicate listeners
let initialized = false;

/**
 * Initialize automatic tracking via data attributes
 * Call once in app layout/root component
 */
export function initAutoTracking(debug: boolean = false): void {
  if (typeof window === "undefined") {
    console.warn("Auto-tracker: Window not available (SSR)");
    return;
  }

  if (initialized) {
    console.warn("Auto-tracker: Already initialized, skipping");
    return;
  }

  initialized = true;

  if (debug) {
    console.log("🎯 Auto-tracker: Initializing...");
  }

  // Explicit tracking takes precedence over contextual article links.
  document.addEventListener("click", (e) => {
    if (!(e.target instanceof Element)) return;

    const target = e.target.closest("[data-track-event]");
    if (!target) {
      const link = e.target.closest("article a[href]");
      if (!link || link.closest("[data-ezychat-card]")) return;

      let destination: URL;
      try {
        destination = new URL(link.getAttribute("href") || "");
      } catch {
        return;
      }

      if (destination.protocol !== "https:" ||
          !["ezychat.ai", "www.ezychat.ai"].includes(destination.hostname)) return;

      trackEvent("ezychat_click", {
        location: "blog_contextual",
        source_path: window.location.pathname,
        destination_path: destination.pathname,
      });
      return;
    }

    const eventName = target.getAttribute("data-track-event");
    if (!eventName) return;

    const location = target.getAttribute("data-track-location") || "unknown";
    
    // Collect all data-track-* attributes as additional data
    const additionalData: Record<string, any> = {};
    Array.from(target.attributes).forEach((attr) => {
      if (attr.name.startsWith("data-track-") &&
          attr.name !== "data-track-event" &&
          attr.name !== "data-track-location") {
        const key = attr.name.replace("data-track-", "").replace(/-/g, "_");
        additionalData[key] = attr.value;
      }
    });

    if (debug) {
      console.log("🎯 Click tracked:", eventName, { location, ...additionalData });
    }

    // Route to appropriate tracking function based on event name
    switch (eventName) {
      case "contact_whatsapp_click":
        trackContactWhatsApp(location, additionalData);
        // "Buy Claude on a PO" CTAs (ClaudeBuyCTA, the calculator buy strip) use locations
        // ending _buy / _buy_inline / _buy_cta. Send a dedicated event too, so GA4 can count
        // buy intent as its own key event: the GA4 UI can no longer derive an event from a
        // parameter condition. Keep this pattern in step with ClaudeBuyCTA's location suffixes.
        if (/_buy(_inline|_cta)?$/.test(location)) {
          trackEvent("claude_buy_intent", {
            category: "conversion",
            label: location,
            method: "whatsapp",
          });
        }
        break;
        
        
      case "contact_email_click":
        trackContactEmail(location, additionalData);
        break;
        
      case "contact_phone_click":
        trackContactPhone(location, additionalData);
        break;
        
      default:
        // Fallback for custom events
        trackEvent(eventName, {
          category: additionalData.category || "engagement",
          label: location,
          ...additionalData,
        });
    }
  }, { passive: true });

  console.log("✅ Auto-tracker: Initialized successfully");
}

/**
 * Helper for TypeScript: Get tracking attributes with type hints
 * 
 * Usage:
 * <a {...getTrackingAttrs("contact_whatsapp_click", "floating_button", { phone: "..." })}>
 */
export function getTrackingAttrs(
  event: string,
  location: string,
  additionalData?: Record<string, string>
): Record<string, string> {
  const attrs: Record<string, string> = {
    "data-track-event": event,
    "data-track-location": location,
  };

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      attrs[`data-track-${key.replace(/_/g, "-")}`] = value;
    });
  }

  return attrs;
}

/**
 * Debug mode: Log all tracking events to console
 */
export function enableTrackingDebug(): void {
  if (typeof window !== "undefined") {
    (window as any).__TRACKING_DEBUG__ = true;
    console.log("🐛 Tracking debug mode enabled");
  }
}

/**
 * Check if auto-tracking is initialized
 */
export function isAutoTrackingInitialized(): boolean {
  return initialized;
}
